import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Tour } from '../../types';
import { useTheme } from '../../contexts/ThemeContext';

interface SafariWheelProps {
    tours: Tour[];
    categoryName: string;
    isVisible: boolean;
}

const SafariWheel: React.FC<SafariWheelProps> = ({ tours, categoryName, isVisible }) => {
    const { actualTheme } = useTheme();
    const [rotation, setRotation] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const [rotationSpeed, setRotationSpeed] = useState(2.0); // Start fast
    const startTimeRef = React.useRef<number>(Date.now());

    // Reset speed when wheel becomes visible
    useEffect(() => {
        if (isVisible) {
            setRotationSpeed(2.0); // Reset to fast speed
            startTimeRef.current = Date.now();
        }
    }, [isVisible]);

    // Continuous rotation animation with deceleration
    useEffect(() => {
        if (!isVisible || isPaused || tours.length === 0) return;

        const interval = setInterval(() => {
            // Calculate elapsed time since wheel appeared
            const elapsed = (Date.now() - startTimeRef.current) / 1000; // in seconds

            // Deceleration curve: fast start (2.0), slow to minimum (0.3) over 5 seconds
            const minSpeed = 0.3;
            const maxSpeed = 2.0;
            const decelerationTime = 5; // seconds to reach minimum speed

            const currentSpeed = Math.max(
                minSpeed,
                maxSpeed - (maxSpeed - minSpeed) * Math.min(elapsed / decelerationTime, 1)
            );

            setRotationSpeed(currentSpeed);
            setRotation((prev) => (prev + currentSpeed) % 360);
        }, 30); // ~60fps

        return () => clearInterval(interval);
    }, [isVisible, isPaused, tours.length]);

    if (!isVisible || tours.length === 0) return null;

    const radius = 220; // Distance from center
    const cardCount = Math.min(tours.length, 6); // Show max 6 tours in wheel

    return (
        <div
            className="absolute inset-0 z-50 pointer-events-none flex items-center justify-center"
            style={{
                perspective: '1000px',
            }}
        >
            {/* Circular wheel container */}
            <div
                className="relative"
                style={{
                    width: `${radius * 2 + 160}px`,
                    height: `${radius * 2 + 200}px`,
                }}
            >
                {tours.slice(0, cardCount).map((tour, index) => {
                    // Calculate position on circle
                    const angle = (360 / cardCount) * index + rotation;
                    const radian = (angle * Math.PI) / 180;
                    const x = Math.cos(radian) * radius;
                    const y = Math.sin(radian) * radius;

                    // Calculate opacity based on position (fade at bottom)
                    const opacity = Math.max(0.3, Math.min(1, (Math.sin(radian) + 1) / 2 + 0.3));

                    // Calculate scale based on position (larger at top/front)
                    const scale = 0.7 + (Math.sin(radian) + 1) / 4;

                    return (
                        <Link
                            key={tour.id}
                            to={`/tours/${tour.id}`}
                            className="absolute pointer-events-auto group/card"
                            style={{
                                left: '50%',
                                top: '50%',
                                transform: `translate(-50%, -50%) translate(${x}px, ${y}px) scale(${scale})`,
                                opacity: opacity,
                                transition: 'transform 0.3s ease, opacity 0.3s ease',
                                zIndex: Math.floor(scale * 100),
                            }}
                            onMouseEnter={() => setIsPaused(true)}
                            onMouseLeave={() => setIsPaused(false)}
                        >
                            {/* Mini Tour Card */}
                            <div
                                className={`safari-wheel-card rounded-2xl overflow-hidden shadow-2xl border-2 backdrop-blur-sm ${actualTheme === 'dark'
                                    ? 'bg-slate-800 border-slate-700/50'
                                    : 'bg-white border-white/50'
                                    }`}
                                style={{
                                    width: '140px',
                                    height: '180px',
                                    animation: 'float 3s ease-in-out infinite',
                                    animationDelay: `${index * 0.5}s`,
                                }}
                            >
                                {/* Tour Image */}
                                <div className="relative h-24 overflow-hidden">
                                    {tour.images && tour.images.length > 0 ? (
                                        <img
                                            src={tour.images[0]}
                                            alt={tour.title}
                                            className="w-full h-full object-cover group-hover/card:scale-110 transition-transform duration-500"
                                        />
                                    ) : (
                                        <div className={`w-full h-full bg-gradient-to-br ${actualTheme === 'dark'
                                            ? 'from-primary-800 to-primary-900'
                                            : 'from-primary-200 to-primary-300'
                                            }`}></div>
                                    )}

                                    {/* Gradient overlay */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>

                                    {/* Price badge */}
                                    <div className="absolute top-2 right-2 bg-primary-800 text-white px-2 py-1 rounded-full text-xs font-bold shadow-lg">
                                        ${tour.price.toLocaleString()}
                                    </div>
                                </div>

                                {/* Tour Info */}
                                <div className="p-3">
                                    <h4 className={`text-xs font-bold line-clamp-2 mb-2 transition-colors ${actualTheme === 'dark'
                                        ? 'text-slate-200 group-hover/card:text-primary-400'
                                        : 'text-gray-900 group-hover/card:text-primary-600'
                                        }`}>
                                        {tour.title}
                                    </h4>

                                    <div className={`flex items-center justify-between text-xs ${actualTheme === 'dark' ? 'text-slate-400' : 'text-gray-600'
                                        }`}>
                                        <span className="flex items-center">
                                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                                            </svg>
                                            {tour.duration}
                                        </span>
                                        <span className="flex items-center text-yellow-500">
                                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                            </svg>
                                            {tour.rating}
                                        </span>
                                    </div>

                                    {/* Hover indicator */}
                                    <div className="mt-2 text-center opacity-0 group-hover/card:opacity-100 transition-opacity">
                                        <span className={`text-xs font-medium ${actualTheme === 'dark' ? 'text-primary-400' : 'text-primary-600'
                                            }`}>
                                            Click to view →
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </Link>
                    );
                })}

                {/* Center glow effect */}
                <div
                    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 bg-primary-500/10 dark:bg-primary-400/10 rounded-full blur-3xl pointer-events-none"
                    style={{
                        animation: 'pulse 4s ease-in-out infinite',
                    }}
                ></div>
            </div>

            {/* CSS Animations */}
            <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }

        .safari-wheel-card {
          will-change: transform, opacity;
        }

        .safari-wheel-card:hover {
          transform: scale(1.1) !important;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
        }
      `}</style>
        </div>
    );
};

export default SafariWheel;
